import { useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';

/**
 * WeeklyHoursEditor — 每週各天時段表（轉真人時段用；開場白日夜界線之後想升級也可重用）
 *
 *   value = { 0: [{start,end}], 1: [...], ..., 6: [...] }   key = JS getDay()（0=週日），空陣列 = 該日休
 *   每天一張卡：標題列（星期 / 開關 / 摘要 / 新增時段），時段一段一行往下長（Modal 620px 寬塞不下橫排三段）。
 *   關掉某天：windows 清空但暫存在 ref，再開回來不用重填。
 *   readOnly：沿用開場白界線時唯讀灰顯。
 */
export const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DEFAULT_WIN = { start: '09:00', end: '18:00' };
const HHMM = /^\d{2}:\d{2}$/;

export function emptyWeekly() {
    return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

/** 舊寫法（全週一組 + weekendAsNight）→ 每週表；載入舊資料 / 沿用開場白界線時展開顯示用 */
export function legacyToWeekly({ dayStart, dayEnd, windows, weekendAsNight } = {}) {
    const base = Array.isArray(windows) && windows.length
        ? windows.map((w) => ({ start: w.start, end: w.end }))
        : [{ start: dayStart || '09:00', end: dayEnd || '18:30' }];
    const wknd = weekendAsNight !== false ? [] : base;
    return { 1: clone(base), 2: clone(base), 3: clone(base), 4: clone(base), 5: clone(base), 6: clone(wknd), 0: clone(wknd) };
}
const clone = (ws) => ws.map((w) => ({ ...w }));

/** 逐段驗證：回 { ok, errors: { [day]: { [idx]: 'invalid' | 'overlap' } }, anyOpen } */
export function validateWeekly(weekly) {
    const errors = {};
    let anyOpen = false;
    for (const d of DAY_ORDER) {
        const ws = (weekly && weekly[d]) || [];
        if (ws.length) anyOpen = true;
        const idxSorted = ws.map((w, i) => i).sort((a, b) => (ws[a].start < ws[b].start ? -1 : 1));
        for (let k = 0; k < idxSorted.length; k++) {
            const i = idxSorted[k];
            const w = ws[i];
            if (!HHMM.test(w.start || '') || !HHMM.test(w.end || '') || w.start >= w.end) {
                (errors[d] ||= {})[i] = 'invalid';
                continue;
            }
            if (k > 0) {
                const prev = ws[idxSorted[k - 1]];
                if (HHMM.test(prev.end || '') && w.start < prev.end) (errors[d] ||= {})[i] = 'overlap';
            }
        }
    }
    return { ok: Object.keys(errors).length === 0 && anyOpen, errors, anyOpen };
}

/** 依時區取現在的 { hhmm, weekday }；tz 空 → 瀏覽器本地 */
export function nowPartsInTz(tz) {
    const d = new Date();
    try {
        if (tz) {
            let hhmm = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
            if (hhmm === '24:00') hhmm = '00:00';
            const wd = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d);
            return { hhmm, weekday: { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[wd] ?? 0 };
        }
    } catch (_) { /* 壞時區 → 本地 */ }
    const z = (n) => String(n).padStart(2, '0');
    return { hhmm: `${z(d.getHours())}:${z(d.getMinutes())}`, weekday: d.getDay() };
}

export function weeklyOpenAt(weekly, { hhmm, weekday }) {
    return ((weekly && weekly[weekday]) || []).some((w) => hhmm >= w.start && hhmm < w.end);
}

/** 星期名稱走 Intl，不用 8 語系 × 7 個 key */
export function weekdayName(d, lang) {
    try {
        // 2026-08-23 是週日；+d 天就是對應星期
        return new Intl.DateTimeFormat(lang || undefined, { weekday: 'short' }).format(new Date(2026, 7, 23 + d));
    } catch (_) { return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]; }
}

const summarize = (ws) => ws.map((w) => `${w.start}–${w.end}`).join('、');

export function WeeklyHoursEditor({ value, onChange, readOnly = false, lang, t, errors = {} }) {
    const weekly = value || emptyWeekly();
    const remembered = useRef({});   // 關掉某天時暫存，開回來還原

    const setDay = (d, ws) => onChange({ ...weekly, [d]: ws });
    const toggleDay = (d) => {
        const ws = weekly[d] || [];
        if (ws.length) { remembered.current[d] = ws; setDay(d, []); }
        else setDay(d, remembered.current[d]?.length ? remembered.current[d] : [{ ...DEFAULT_WIN }]);
    };
    const addWin = (d) => {
        const ws = weekly[d] || [];
        // 接在最後一段後面 1 小時起、長 2 小時；超過 23:00 就給 DEFAULT
        const last = ws[ws.length - 1];
        let next = { ...DEFAULT_WIN };
        if (last && HHMM.test(last.end)) {
            const [h] = last.end.split(':').map(Number);
            if (h + 1 <= 21) next = { start: `${String(h + 1).padStart(2, '0')}:00`, end: `${String(h + 3).padStart(2, '0')}:00` };
        }
        setDay(d, [...ws, next]);
    };
    const updWin = (d, i, patch) => setDay(d, (weekly[d] || []).map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
    const rmWin = (d, i) => setDay(d, (weekly[d] || []).filter((_, idx) => idx !== i));

    const inputCls = 'px-2 py-1 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white tabular-nums disabled:bg-cream-50 disabled:text-ink-400';

    return (
        <div className={['space-y-2', readOnly ? 'opacity-60' : ''].join(' ')}>
            {DAY_ORDER.map((d) => {
                const ws = weekly[d] || [];
                const on = ws.length > 0;
                const dayErr = errors[d] || {};
                return (
                    <div key={d} className={['rounded-md border px-3 py-2.5', on ? 'border-cream-200 bg-white' : 'border-dashed border-cream-300 bg-transparent'].join(' ')}>
                        <div className="flex items-center gap-2.5 min-h-[28px]">
                            <span className={['text-sm font-medium w-9 shrink-0', on ? 'text-ink-800' : 'text-ink-400'].join(' ')}>{weekdayName(d, lang)}</span>
                            <button type="button" role="switch" aria-checked={on} disabled={readOnly} onClick={() => toggleDay(d)}
                                className={['relative w-8 h-[18px] rounded-full transition shrink-0 disabled:cursor-not-allowed', on ? 'bg-ink-900' : 'bg-cream-300'].join(' ')}>
                                <span className={['absolute top-[2px] size-[14px] rounded-full bg-white transition-all', on ? 'left-[16px]' : 'left-[2px]'].join(' ')} />
                            </button>
                            <span className={['text-[11px] font-mono tabular-nums truncate', on ? 'text-ink-500' : 'text-ink-400'].join(' ')}>
                                {on ? summarize(ws) : t('greetingSchedule.transfer.dayOff')}
                            </span>
                            {on && !readOnly && (
                                <button type="button" onClick={() => addWin(d)}
                                    className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md border border-dashed border-cream-300 text-ink-500 hover:border-accent-400 hover:text-accent-700 transition">
                                    <Plus size={11} /> {t('greetingSchedule.transfer.addWindow')}
                                </button>
                            )}
                        </div>
                        {on && (
                            <div className="mt-2 space-y-1.5">
                                {ws.map((w, i) => {
                                    const err = dayErr[i];
                                    return (
                                        <div key={i}>
                                            <div className={['flex items-center gap-2 rounded-md px-2 py-1.5', err ? 'bg-red-50 ring-1 ring-red-200' : 'bg-cream-100/50'].join(' ')}>
                                                <input type="time" value={w.start} disabled={readOnly} onChange={(e) => updWin(d, i, { start: e.target.value })} className={inputCls} />
                                                <span className="text-ink-300 font-mono text-xs">—</span>
                                                <input type="time" value={w.end} disabled={readOnly} onChange={(e) => updWin(d, i, { end: e.target.value })} className={inputCls} />
                                                {!readOnly && (
                                                    <button type="button" onClick={() => rmWin(d, i)} title={t('greetingSchedule.transfer.removeWindow')}
                                                        className="ml-auto size-7 shrink-0 inline-flex items-center justify-center rounded-md text-ink-300 hover:text-red-600 hover:bg-red-50 transition">
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                            {err && <div className="text-[11px] text-red-600 px-2 mt-0.5">{err === 'overlap' ? t('greetingSchedule.transfer.overlap') : t('greetingSchedule.transfer.invalidWindow')}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/** 快速套用 preset（純前端，一鍵覆寫 7 天） */
export const WEEKLY_PRESETS = [
    { key: 'weekday918', build: () => legacyToWeekly({ dayStart: '09:00', dayEnd: '18:00', weekendAsNight: true }) },
    { key: 'weekdayLunch', build: () => legacyToWeekly({ windows: [{ start: '09:00', end: '12:00' }, { start: '13:30', end: '17:30' }], weekendAsNight: true }) },
    { key: 'daily1020', build: () => legacyToWeekly({ dayStart: '10:00', dayEnd: '20:00', weekendAsNight: false }) },
    { key: 'allDay', build: () => legacyToWeekly({ dayStart: '00:00', dayEnd: '23:59', weekendAsNight: false }) },
    { key: 'copyMon', build: (cur) => { const m = clone((cur && cur[1]) || []); return { ...cur, 2: clone(m), 3: clone(m), 4: clone(m), 5: clone(m) }; } },
];

/** 時區選單：常用亞太 + UTC；顯示 (GMT+08:00) Asia/Taipei，offset 用 Intl 現算，不用 i18n */
export const TIMEZONE_OPTIONS = [
    'Asia/Taipei', 'Asia/Bangkok', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kuala_Lumpur', 'Asia/Ho_Chi_Minh', 'Asia/Jakarta', 'Asia/Manila', 'Australia/Sydney', 'UTC',
];
export function tzLabel(tz) {
    try {
        const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName');
        const off = (part?.value || 'GMT').replace(/^GMT$/, 'GMT+00:00');
        return `(${off}) ${tz}`;
    } catch (_) { return tz; }
}
